import { ReactElement } from "react";

import pkg from "../../../package.json";
import styles from "./index.module.css";
import { useDrawings } from "../../state/drawings";
import { useSettings } from "../../state/settings";
import { useNavigate } from "../../lib/navigate";
import { useKeyboardShortcuts } from "../../lib/keyboard";
import { importDrawings, exportDrawings } from "../../lib/data";
import Header from "../shared/Header";
import TwoColSection from "./TwoColSection";
import OneColSection from "./OneColSection";
import Dropdown from "../shared/Dropdown";
import IconButton from "../shared/Icon/Button";
import IconLink from "../shared/Icon/Link";

export default function SettingsPage(): ReactElement {
  const { settings, updateSettings } = useSettings();
  const { drawings, insert, removeAll } = useDrawings();

  function loadImport(): void {
    importDrawings().then((drawings) => insert(drawings));
  }

  function deleteAll(): void {
    const confirm = window.prompt(
      `\nWAIT! This cannot be undone.\n\nType 'yes' to delete all drawings.`
    );
    if (confirm?.toLowerCase().replace(/\s+/g, "") === "yes") {
      removeAll();
    }
  }

  const navigate = useNavigate();
  useKeyboardShortcuts({
    close: () => navigate(-1),
    settings: () => navigate(-1),
  });

  return (
    <div className={styles.wrapper}>
      <Header title={"settings"} modal={true} />

      <div className={styles.layout}>
        <div className={styles.settings}>
          <h1>editor</h1>

          <TwoColSection
            children={[
              {
                left: (
                  <IconButton
                    name={settings.gridLines ? "checkbox-on" : "checkbox-off"}
                    onClick={() =>
                      updateSettings({ gridLines: !settings.gridLines })
                    }
                  />
                ),
                right: <p>grid lines</p>,
              },

              {
                left: (
                  <IconButton
                    name={settings.axisLabels ? "checkbox-on" : "checkbox-off"}
                    onClick={() =>
                      updateSettings({ axisLabels: !settings.axisLabels })
                    }
                  />
                ),
                right: <p>axis labels</p>,
              },

              {
                left: (
                  <IconButton
                    name={settings.toolShadow ? "checkbox-on" : "checkbox-off"}
                    onClick={() =>
                      updateSettings({ toolShadow: !settings.toolShadow })
                    }
                  />
                ),
                right: <p>tool shadow</p>,
              },
              {
                left: (
                  <IconButton
                    name={settings.leftHanded ? "checkbox-on" : "checkbox-off"}
                    onClick={() =>
                      updateSettings({ leftHanded: !settings.leftHanded })
                    }
                  />
                ),
                right: <p>left-handed</p>,
              },
            ]}
          />

          <h1>preview</h1>

          <TwoColSection
            children={[
              {
                left: (
                  <IconButton
                    name={
                      settings.thumbnailLarge ? "checkbox-on" : "checkbox-off"
                    }
                    onClick={() =>
                      updateSettings({
                        thumbnailLarge: !settings.thumbnailLarge,
                      })
                    }
                  />
                ),
                right: <p>large thumbnail</p>,
              },

              {
                left: (
                  <IconButton
                    name={
                      settings.thumbnailMedium ? "checkbox-on" : "checkbox-off"
                    }
                    onClick={() =>
                      updateSettings({
                        thumbnailMedium: !settings.thumbnailMedium,
                      })
                    }
                  />
                ),
                right: <p>medium thumbnail</p>,
              },

              {
                left: (
                  <IconButton
                    name={
                      settings.thumbnailSmall ? "checkbox-on" : "checkbox-off"
                    }
                    onClick={() =>
                      updateSettings({
                        thumbnailSmall: !settings.thumbnailSmall,
                      })
                    }
                  />
                ),
                right: <p>small thumbnail</p>,
              },
            ]}
          />

          <h1>download</h1>

          <OneColSection
            children={[
              <Dropdown
                label={"resolution"}
                key={"resolution"}
                selected={settings.resolution}
                options={[32, 64, 128, 256, 512, 1024].map((resolution) => {
                  return {
                    name: `${resolution} x ${resolution}`,
                    value: resolution,
                    callback: () => updateSettings({ resolution }),
                  };
                })}
              />,

              <Dropdown
                label={"use transparent fill"}
                key={"use transparent fill"}
                selected={settings.transparentFill}
                options={[
                  {
                    name: "----",
                    value: undefined,
                    callback: () =>
                      updateSettings({ transparentFill: undefined }),
                  },
                  {
                    name: "for black pixels",
                    value: "black",
                    callback: () =>
                      updateSettings({ transparentFill: "black" }),
                  },
                  {
                    name: "for white pixels",
                    value: "white",
                    callback: () =>
                      updateSettings({ transparentFill: "white" }),
                  },
                ]}
              />,
            ]}
          />

          <h1>app data</h1>

          <TwoColSection
            children={[
              {
                left: (
                  <IconButton
                    name={"export"}
                    disabled={drawings.length === 0}
                    onClick={() => exportDrawings(drawings)}
                  />
                ),
                right: (
                  <div>
                    <p>export drawings to a local backup</p>
                  </div>
                ),
              },

              {
                left: <IconButton name={"import"} onClick={loadImport} />,
                right: <p>import drawings from a local backup</p>,
              },

              {
                left: (
                  <IconButton
                    name={"delete-all"}
                    disabled={drawings.length === 0}
                    onClick={deleteAll}
                  />
                ),
                right: <p>delete all drawings</p>,
              },
            ]}
          />

          <h1>about</h1>

          <TwoColSection
            children={[
              {
                left: <IconLink name={"copyright"} href={"https://awjin.me"} />,
                right: (
                  <>
                    <p>awjin ahn 2021</p>
                  </>
                ),
              },
              {
                // TODO: changelog?
                // left: <IconLink name={"info"} href={""} />,
                right: (
                  <>
                    <p>version {pkg.version}</p>
                  </>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
